import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { getAllCurrencies } from "../../redux/Actions/AuthAction";
import { getCurrencyByCodeAction } from "../../redux/Actions/CurrencyActions";
import { escrowAPI, EscrowStatus } from "../../services/escrowAPI";
import { createCredit } from "../../redux/Slices/creditSlice";
import {
  useCurrencyDetection,
  getCachedCurrencyPreference,
  getCachedCurrencyCode,
  getCachedCurrencySymbol,
  setCachedCurrencyPreference,
  getCurrencySymbol,
} from "../../lib/currencyUtils";
import { convertCurrency, getCurrencyById, getCurrencyByCode } from "../../lib/currencyConversion";
import { Button } from "../ui/Button";
import CurrencyDropdown from "../ui/CurrencyDropdown";
import { X, Loader2, CheckCircle, AlertCircle, CreditCard, User, Mail, Wallet } from "lucide-react";
import { checkMinPurchaseQuoteThreshold, getPlatformFeeForQuote } from "../../utils/categoryFormSettingsUtils";
import { RoleEnum } from "../../modules/shared/model/role.enum";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import PaymentMethodSelector from "./PaymentMethodSelector";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

interface CreditPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (paymentData: any) => void;
  planAmount: number;
  planName: string;
  currencySymbol?: string;
  selectedPlan?: any; // The full plan object with subscription plan details
  selectedCurrencyCode?: string; // The currency code selected in credits page (e.g., "USD", "CZK")
  baseAmount?: number; // The base amount in EUR
}


// Inner component that has access to Stripe
const CreditPaymentModalContent: React.FC<CreditPaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  planAmount,
  planName,
  currencySymbol = "$",
  selectedPlan,
  selectedCurrencyCode = "EUR",
  baseAmount
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { user, currencies } = useAppSelector((state: any) => state.auth);
  const { setting } = useAppSelector((state: any) => state.categoryFormSettings);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isDetectingCurrency, setIsDetectingCurrency] = useState(false);
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  const [walletCurrency, setWalletCurrency] = useState<string>("USD");
  const [checkingBalance, setCheckingBalance] = useState(false);
  const [useExistingFunds, setUseExistingFunds] = useState(false);
  const [showMinThresholdDialog, setShowMinThresholdDialog] = useState(false);
  const [thresholdInfo, setThresholdInfo] = useState<{ currentTotal: number, threshold: number } | null>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [isNewCard, setIsNewCard] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<{id: string; name: string; code: string} | null>(null);
  const [displayAvailableBalance, setDisplayAvailableBalance] = useState<number>(0);
  const [displayBalanceCurrency, setDisplayBalanceCurrency] = useState<string>("USD");
  const cachedCurrencyId = getCachedCurrencyPreference();
  const cachedCurrencyCode = getCachedCurrencyCode();
  const cachedCurrencySymbol = getCachedCurrencySymbol();
  const isFallbackPricing = Boolean(selectedPlan?.isFallbackPricing);
  const fallbackCurrencyCode = "EUR";
  const fallbackCurrencySymbol = getCurrencySymbol(fallbackCurrencyCode);
  const fallbackCurrencyId = selectedPlan?.selectedCurrencyId || cachedCurrencyId || fallbackCurrencyCode;
  const effectiveSelectedCurrencyCode = (isFallbackPricing
    ? fallbackCurrencyCode
    : selectedPlan?.selectedCurrencyCode || selectedCurrencyCode || cachedCurrencyCode || fallbackCurrencyCode
  )?.toUpperCase();
  const matchingCurrency = currencies?.find((c: any) =>
    c.initial === effectiveSelectedCurrencyCode ||
    c.code === effectiveSelectedCurrencyCode
  );
  const effectiveSelectedCurrencyId = isFallbackPricing
    ? fallbackCurrencyId
    : selectedPlan?.selectedCurrencyId || matchingCurrency?.value || cachedCurrencyId || "2";
  const initialResolvedSymbol =
    (isFallbackPricing ? fallbackCurrencySymbol : undefined) ||
    selectedPlan?.currencySymbol ||
    cachedCurrencySymbol ||
    currencySymbol ||
    getCurrencySymbol(effectiveSelectedCurrencyCode || fallbackCurrencyCode);
  const [resolvedCurrencySymbol, setResolvedCurrencySymbol] = useState<string>(
    initialResolvedSymbol
  );

  // Calculate platform fee for quote purchase
  const platformFee = getPlatformFeeForQuote(planAmount, setting);
  const totalWithFee = planAmount + platformFee;

  // Initialize formData with the selected currency from credits page
  const [formData, setFormData] = useState(() => ({
    currency: effectiveSelectedCurrencyCode,
    selectedCurrencyId: effectiveSelectedCurrencyId,
  }));

  // Function to check available balance
  const checkAvailableBalance = async () => {
    if (!user?.id) return;

    setCheckingBalance(true);
    try {
      const balanceData = await escrowAPI.getUserEscrowBalance(user.id, {
        page: 1,
        limit: 1,
        filters: {
          escrowStatus: [EscrowStatus.RELEASED, EscrowStatus.WITHDRAWN, EscrowStatus.USER_FUNDED]
        }
      });
      const balance = (balanceData.availableBalance || 0) + (balanceData.balance || 0);
      const currency = balanceData.currency || "USD";
      
      setAvailableBalance(balance);
      setWalletCurrency(currency);
    } catch (error) {
      console.error("Error checking available balance:", error);
      setAvailableBalance(0);
      setWalletCurrency("USD");
    } finally {
      setCheckingBalance(false);
    }
  };

  // Convert available balance to display currency
  const convertAvailableBalance = () => {
    // Use formData.currency (current selected currency in modal) not prop selectedCurrencyCode
    const currentCurrencyCode = formData.currency || selectedCurrencyCode;
    
    if (!currencies || currencies.length === 0 || !walletCurrency || !currentCurrencyCode) {
      setDisplayAvailableBalance(availableBalance);
      setDisplayBalanceCurrency(walletCurrency);
      return;
    }

    // If wallet currency is same as selected currency, no conversion needed
    if (walletCurrency === currentCurrencyCode) {
      setDisplayAvailableBalance(availableBalance);
      setDisplayBalanceCurrency(walletCurrency);
      return;
    }

    try {
      const walletCurrObj = getCurrencyByCode(walletCurrency, currencies);
      const selectedCurrObj = getCurrencyByCode(currentCurrencyCode, currencies);

      if (walletCurrObj && selectedCurrObj) {
        const conversion = convertCurrency(availableBalance, walletCurrObj.value, selectedCurrObj.value, currencies);
        setDisplayAvailableBalance(conversion.convertedAmount);
        setDisplayBalanceCurrency(currentCurrencyCode);
      } else {
        setDisplayAvailableBalance(availableBalance);
        setDisplayBalanceCurrency(walletCurrency);
      }
    } catch (error) {
      console.error("Error converting balance:", error);
      setDisplayAvailableBalance(availableBalance);
      setDisplayBalanceCurrency(walletCurrency);
    }
  };

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Load currencies if not already loaded
      if (!currencies || currencies.length === 0) {
        dispatch(getAllCurrencies({ isShowLoader: false }));
      }
      // Check available balance
      checkAvailableBalance();

      // Check min purchase quote threshold
      if (user?.id) {
        checkMinPurchaseQuoteThreshold(user.id, setting).then((result) => {
          if (result.reached && result.threshold > 0) {
            setThresholdInfo({ currentTotal: result.currentTotal, threshold: result.threshold });
            setShowMinThresholdDialog(true);
          }
        });
      }
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, dispatch, currencies, user?.id, setting]);

  // Sync selectedCurrency with formData.selectedCurrencyId
  useEffect(() => {
    if (formData.selectedCurrencyId && currencies && currencies.length > 0) {
      if (selectedCurrency?.id !== formData.selectedCurrencyId) {
        const currency = currencies.find((c: any) => c.value === formData.selectedCurrencyId);
        if (currency) {
          setSelectedCurrency({
            id: formData.selectedCurrencyId,
            name: currency.label || "USD",
            code: currency.initial || "USD"
          });
        }
      }
    }
  }, [formData.selectedCurrencyId, currencies, selectedCurrency?.id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const code =
      (formData.currency || selectedCurrencyCode || "EUR")?.toString().toUpperCase();

    if (!code) {
      return;
    }

    let isCancelled = false;

    const fetchCurrencySymbol = async () => {
      const latestCachedSymbol = getCachedCurrencySymbol();
      if (isFallbackPricing) {
        const symbol = fallbackCurrencySymbol;
        setResolvedCurrencySymbol(symbol);
        if (formData.selectedCurrencyId) {
          setCachedCurrencyPreference(formData.selectedCurrencyId, {
            currencyCode: fallbackCurrencyCode,
            currencySymbol: symbol,
          });
        }
        return;
      }
      try {
        const currencyDetails = await getCurrencyByCodeAction(code);
        if (!isCancelled) {
          const symbol =
            currencyDetails?.symbol ||
            currencyDetails?.currencySymbol ||
            latestCachedSymbol ||
            getCurrencySymbol(code);
          setResolvedCurrencySymbol(symbol);
          if (formData.selectedCurrencyId) {
            setCachedCurrencyPreference(formData.selectedCurrencyId, {
              currencyCode: code,
              currencySymbol: symbol,
            });
          }
        }
      } catch (_error) {
        if (!isCancelled) {
          const fallbackSymbol = latestCachedSymbol || getCurrencySymbol(code);
          setResolvedCurrencySymbol(fallbackSymbol);
          if (formData.selectedCurrencyId) {
            setCachedCurrencyPreference(formData.selectedCurrencyId, {
              currencyCode: code,
              currencySymbol: fallbackSymbol,
            });
          }
        }
      }
    };

    const immediateSymbol = getCachedCurrencySymbol() || getCurrencySymbol(code);
    setResolvedCurrencySymbol(immediateSymbol);
    fetchCurrencySymbol();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, formData.currency, selectedCurrencyCode]);

  // Convert available balance when balance, wallet currency, or selected currency changes
  useEffect(() => {
    if (availableBalance >= 0 && walletCurrency && currencies && currencies.length > 0) {
      convertAvailableBalance();
    }
  }, [availableBalance, walletCurrency, formData.currency, selectedCurrencyCode, currencies]);

  // Handle currency selection
  const handleCurrencySelect = (currency: {id: string; name: string; code: string; symbol?: string} | null) => {
    if (isFallbackPricing) {
      return;
    }
    if (currency) {
      setSelectedCurrency(currency);
      setFormData(prev => ({
        ...prev,
        selectedCurrencyId: currency.id,
        currency: currency.code
      }));
      const immediateSymbol =
        currency.symbol || getCachedCurrencySymbol() || resolvedCurrencySymbol;
      setCachedCurrencyPreference(currency.id, {
        currencyCode: currency.code,
        currencySymbol: immediateSymbol,
      });
      setResolvedCurrencySymbol(immediateSymbol);
      
      // Note: selectedCurrencyCode will be updated via props, 
      // but for immediate conversion we need to trigger it here
    }
  };

  // Currency detection effect
  useEffect(() => {
    const detectAndSetCurrency = async () => {
      if (isFallbackPricing) {
        return;
      }
      if (!currencies || currencies.length === 0 || !isOpen) return;

      setIsDetectingCurrency(true);
      try {
        // First check for cached preference
        const cachedCurrencyId = getCachedCurrencyPreference();
        if (cachedCurrencyId) {
          const selectedCurrency = currencies.find((c: any) => c.value === cachedCurrencyId);
          setFormData(prev => ({
            ...prev,
            selectedCurrencyId: cachedCurrencyId,
            currency: selectedCurrency?.initial || "USD"
          }));
          const cachedSymbol = getCachedCurrencySymbol();
          if (cachedSymbol) {
            setResolvedCurrencySymbol(cachedSymbol);
          }
          setIsDetectingCurrency(false);
          return;
        }

        // Detect currency based on user location
        const detectedCurrencyId = await useCurrencyDetection(
          currencies,
          (currencyId, source) => {
            const detectedCurrency = currencies.find((c: any) => c.value === currencyId);
            setCachedCurrencyPreference(currencyId, {
              currencyCode: detectedCurrency?.initial,
              currencySymbol: detectedCurrency?.symbol,
            });
          },
          dispatch // Pass dispatch for backend API call
        );

        const selectedCurrency = currencies.find((c: any) => c.value === detectedCurrencyId);
        setFormData(prev => ({
          ...prev,
          selectedCurrencyId: detectedCurrencyId,
          currency: selectedCurrency?.initial || "USD"
        }));
      } catch (error) {
        console.error("Error detecting currency:", error);
        // Fallback to EUR (base currency)
        const eurCurrency = currencies.find((c: any) => c.initial === "EUR");
        if (eurCurrency) {
          setFormData(prev => ({
            ...prev,
            selectedCurrencyId: eurCurrency.value,
            currency: "EUR"
          }));
        }
      } finally {
        setIsDetectingCurrency(false);
      }
    };

    detectAndSetCurrency();
  }, [currencies, isOpen]);

  // Reset states when modal closes
  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setLoading(false);
    setUseExistingFunds(false);
    setAvailableBalance(0);
    setSelectedPaymentMethodId(null);
    setIsNewCard(false);
    setFormData({
      currency: "USD",
      selectedCurrencyId: "1",
    });
    setResolvedCurrencySymbol(getCurrencySymbol("USD"));
    document.body.style.overflow = "unset";
    onClose();
  };

  if (!mounted || !isOpen) return null;

  const validateForm = () => {
    // If using existing funds, no need to validate card details
    if (useExistingFunds) {
      return true;
    }

    // Check if a payment method is selected
    if (!selectedPaymentMethodId) {
      setError(t("creditPaymentModal.selectPaymentMethod"));
      return false;
    }

    return true;
  };

  // Handle payment method selection
  const handlePaymentMethodSelected = (paymentMethodId: string, isNew: boolean) => {
    setSelectedPaymentMethodId(paymentMethodId);
    setIsNewCard(isNew);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate form first
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      // Determine user role and set appropriate IDs
      const isClient = Number(user?.role?.id) === RoleEnum.client;
      const clientId = isClient ? user.id : null;
      const professionalId = !isClient ? user.id : null;

      let escrow;
      const paymentMethodId = selectedPaymentMethodId;

      if (useExistingFunds) {
        // Using existing funds - create escrow with used_quote_purchased status
        const escrowData = {
          amount: planAmount, // Base amount (no platform fee)
          platformFee: 0, // No platform fee
          totalAmount: planAmount, // Total amount (same as base amount)
          withdrawlAmount: planAmount, // Amount available for withdrawal
          currency: formData.selectedCurrencyId, // Currency ID
          status: "used_quote_purchased" // Set status directly
        };

        if (isClient) {
          escrowData["clientId"] = clientId;
        } else {
          escrowData["professionalId"] = professionalId;
        }

        try {
          escrow = await escrowAPI.createEscrow(escrowData);
          await escrowAPI.updateEscrow(escrow.id, {
            status: "used_quote_purchased"
          });
        } catch (escrowError: any) {
          console.error("Escrow creation error:", escrowError);
          throw new Error("Failed to create escrow record. Please try again.");
        }
      } else {
        // Using card payment - use addFunds approach like PaymentModal
        if (!selectedPaymentMethodId) {
          throw new Error("No payment method selected");
        }

        // Create escrow record with pending status (with platform fee for quote purchase)
        const escrowData = {
          amount: planAmount, // Base amount
          platformFee: platformFee, // Platform fee for quote purchase
          totalAmount: totalWithFee, // Total amount including platform fee
          withdrawlAmount: planAmount, // Amount available for withdrawal (base amount only)
          currency: formData.selectedCurrencyId, // Currency ID
          // No status specified = pending
        };

        if (isClient) {
          escrowData["clientId"] = clientId;
        } else {
          escrowData["professionalId"] = professionalId;
        }

        try {
          escrow = await escrowAPI.createEscrow(escrowData);
        } catch (escrowError: any) {
          console.error("Escrow creation error:", escrowError);
          throw new Error("Failed to create escrow record. Please try again.");
        }

        // Process payment using addFunds API (same as PaymentModal)
        try {
          // Get the selected currency info
          const selectedCurrency = currencies?.find((c: any) => c.value === formData.selectedCurrencyId);


          // Call addFunds API (same as PaymentModal and AddFundsModal)
          const addFundsData = {
            amount: escrowData.totalAmount,
            currency: selectedCurrency?.code || "usd",
            paymentMethodId: selectedPaymentMethodId,
            notes: `Credit purchase for ${planName}`,
          };

          const result = await escrowAPI.addFunds(addFundsData);

          // Update escrow status to credits_purchased (using the same pattern as PaymentModal)
          await escrowAPI.updateEscrow(escrow.id, {
            status: "credits_purchased",
            stripePaymentIntentId: result.paymentIntentId,
            notes: `Credit purchase for ${planName}`
          });

        } catch (paymentError: any) {
          console.error("Payment processing error:", paymentError);

          // Update escrow status to failed
          try {
            await escrowAPI.updateEscrow(escrow.id, {
              status: "failed"
            });
          } catch (updateError) {
            console.error("Failed to update escrow status:", updateError);
          }

          // Re-throw the error to be handled by the outer catch block
          throw paymentError;
        }
      }

      // Create credit record after successful payment
      if (selectedPlan && user?.id) {
        const creditData = {
          status: { id: 1 }, // Available status
          credit: selectedPlan.selectedArea?.id ? selectedPlan.selectedArea.credits : selectedPlan.credits, // Use plan credits or amount
          escrow: { id: escrow.id },
          user: { id: user.id },
          subscriptionPlan: selectedPlan.id ? { id: selectedPlan.id } : undefined,
          subscriptionPlanArea: selectedPlan.selectedArea?.id ? { id: selectedPlan.selectedArea.id } : undefined
        };

        try {
          await dispatch(createCredit(creditData));
          if (useExistingFunds) {
            await escrowAPI.updateEscrow(escrow.id, {
              status: "used_quote_purchased"
            });
          }
          handleReturnToQuotePurchase();
        } catch (creditError: any) {
          console.error("Credit creation error:", creditError);
          // Don't throw error here as payment was successful
        }
      }

      setSuccess(true);
      setTimeout(() => {
        handleClose();
        onSuccess?.({
          amount: planAmount,
          planName,
          escrowId: escrow.id,
          paymentMethodId: paymentMethodId,
          transactionId: "TXN_" + Date.now(),
          timestamp: new Date().toISOString(),
          usedExistingFunds: useExistingFunds
        });
      }, 2000);

    } catch (err: any) {
      console.error("Credit purchase error:", err);

      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError(t("creditPaymentModal.unexpectedError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToQuotePurchase = () => {
    try {
      const quotePurchaseContext = localStorage.getItem("quotePurchaseContext");
      if (quotePurchaseContext) {
        const context = JSON.parse(quotePurchaseContext);
        // Check if context is recent (within 30 minutes)
        const isRecent = Date.now() - context.timestamp < 30 * 60 * 1000;

        if (isRecent && context.quoteId && context.returnTo) {
          // Clear the stored context
          localStorage.removeItem("quotePurchaseContext");

          // Navigate back to professional page and set up quote purchase modal context
          navigate(context.returnTo, {
            state: {
              openQuotePurchaseModal: true,
              quoteId: context.quoteId,
              amount: context.amount,
              jobId: context.jobId,
              jobTitle: context.jobTitle,
              professionalId: context.professionalId,
              professionalName: context.professionalName,
              isEmergency: context.isEmergency,
              useEmergencyCredits: context.useEmergencyCredits,
              paymentMethod: context.paymentMethod
            }
          });
          return true;
        } else {
          // Clear expired context
          localStorage.removeItem("quotePurchaseContext");
        }
      }
    } catch (error) {
      console.error("Error handling return to quote purchase modal:", error);
      localStorage.removeItem("quotePurchaseContext");
    }
    return false;
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      style={{ backdropFilter: "blur(4px)" }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{t("creditPaymentModal.title")}</h2>
                <p className="text-blue-100 text-sm">{t("creditPaymentModal.subtitle")}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Min Purchase Threshold Dialog */}
        {showMinThresholdDialog && thresholdInfo && (
          <div className="px-6 pb-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                <div>
                  <h4 className="font-medium text-yellow-800">{t("creditPaymentModal.approachingLimit")}</h4>
                  <p className="text-sm text-yellow-600 mt-1">
                    {t("creditPaymentModal.approachingLimitDesc", { 
                      current: `${resolvedCurrencySymbol}${thresholdInfo.currentTotal.toFixed(2)}`,
                      threshold: `${resolvedCurrencySymbol}${thresholdInfo.threshold.toFixed(2)}`
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {success ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center py-8"
            >
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t("creditPaymentModal.successTitle")}</h3>
              <div className="text-gray-600 space-y-2">
                <p>{t("creditPaymentModal.successMessage")}</p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                  <p className="text-sm text-green-800">
                    <strong>{t("creditPaymentModal.plan")}</strong> {planName}
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    <strong>{t("creditPaymentModal.amountPaid")}</strong> {resolvedCurrencySymbol}{planAmount.toFixed(2)} {formData.currency}
                  </p>
                  {baseAmount && baseAmount !== planAmount && (
                    <p className="text-xs text-green-600 mt-1 ml-4">
                      Original: €{baseAmount.toFixed(2)} EUR
                    </p>
                  )}
                  <p className="text-sm text-green-800 mt-1">
                    <strong>{t("creditPaymentModal.platformFee")}</strong> {resolvedCurrencySymbol}{platformFee.toFixed(2)} {formData.currency}
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    <strong>{t("creditPaymentModal.paymentMethod")}</strong> {useExistingFunds ? t("creditPaymentModal.walletFunds") : t("creditPaymentModal.creditCard")}
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    <strong>{t("creditPaymentModal.totalCharged")}</strong> {resolvedCurrencySymbol}{totalWithFee.toFixed(2)} {formData.currency}
                  </p>
                  {useExistingFunds && walletCurrency !== formData.currency && (
                    <p className="text-xs text-green-600 mt-2 ml-4">
                      Deducted from Wallet: {getCurrencySymbol(walletCurrency)}{(() => {
                        const walletCurrObj = getCurrencyByCode(walletCurrency, currencies);
                        const selectedCurrObj = getCurrencyByCode(formData.currency, currencies);
                        if (walletCurrObj && selectedCurrObj) {
                          const conversion = convertCurrency(planAmount, selectedCurrObj.value, walletCurrObj.value, currencies);
                          return `${conversion.convertedAmount.toFixed(2)} ${walletCurrency}`;
                        }
                        return `${planAmount.toFixed(2)} ${walletCurrency}`;
                      })()}
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  {t("creditPaymentModal.creditsAvailable")}
                </p>
              </div>
            </motion.div>
          ) : (
            <div className="space-y-6">
              {/* Plan Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium text-gray-900">{planName}</h3>
                    <p className="text-sm text-gray-500">{t("creditPaymentModal.creditPackage")}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-gray-900">
                      {resolvedCurrencySymbol}{planAmount.toFixed(2)}
                    </p>
                    {baseAmount && baseAmount !== planAmount && (
                      <p className="text-xs text-gray-500">Original: €{baseAmount.toFixed(2)} EUR</p>
                    )}
                    <p className="text-xs text-gray-500">{t("creditPaymentModal.baseAmount")}</p>
                  </div>
                </div>
              </div>

              {/* Available Balance Option */}
              {checkingBalance ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <span className="text-blue-700">{t("creditPaymentModal.checkingBalance")}</span>
                  </div>
                </div>
              ) : displayAvailableBalance >= planAmount ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-green-100 rounded-lg">
                      <Wallet className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-green-800 mb-2">
                        {t("creditPaymentModal.sufficientFunds")}
                      </h3>
                      <p className="text-sm text-green-700 mb-3">
                        {t("creditPaymentModal.availableBalance")} <span className="font-semibold">{getCurrencySymbol(displayBalanceCurrency)}{displayAvailableBalance.toFixed(2)} {displayBalanceCurrency}</span>
                        {walletCurrency !== displayBalanceCurrency && (
                          <span className="text-xs text-green-600 block mt-1">
                            Original: {getCurrencySymbol(walletCurrency)}{availableBalance.toFixed(2)} {walletCurrency}
                          </span>
                        )}
                      </p>
                      {useExistingFunds && formData.currency && walletCurrency && currencies && currencies.length > 0 && (() => {
                        const walletCurrObj = getCurrencyByCode(walletCurrency, currencies);
                        const selectedCurrObj = getCurrencyByCode(formData.currency, currencies);
                        
                        // Only show conversion info if currencies are different
                        if (walletCurrObj && selectedCurrObj && walletCurrency !== formData.currency) {
                          const conversion = convertCurrency(planAmount, selectedCurrObj.value, walletCurrObj.value, currencies);
                          return (
                            <div className="bg-white border border-green-300 rounded-lg p-3 mb-2">
                              <p className="text-xs text-green-800 font-medium mb-1">{t("creditPaymentModal.walletDeduction")}</p>
                              <p className="text-xs text-green-700">
                                Credit Cost: {resolvedCurrencySymbol}{planAmount.toFixed(2)} {formData.currency}
                                <br />
                                Deducted from Wallet: {getCurrencySymbol(walletCurrency)}{conversion.convertedAmount.toFixed(2)} {walletCurrency}
                                <br />
                                Rate: 1 {formData.currency} = {conversion.exchangeRate.toFixed(4)} {walletCurrency}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={useExistingFunds}
                            onChange={(e) => setUseExistingFunds(e.target.checked)}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className="text-sm font-medium text-green-800">
                            {t("creditPaymentModal.useExistingFunds")}
                          </span>
                        </label>
                      </div>
                      {useExistingFunds && (
                        <p className="text-xs text-green-600 mt-2">
                          {t("creditPaymentModal.noCharges")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : displayAvailableBalance > 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-yellow-100 rounded-lg">
                      <Wallet className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-yellow-800 mb-2">
                        {t("creditPaymentModal.partialFunds")}
                      </h3>
                      <p className="text-sm text-yellow-700">
                        {t("creditPaymentModal.availableBalance")} <span className="font-semibold">{getCurrencySymbol(displayBalanceCurrency)}{displayAvailableBalance.toFixed(2)} {displayBalanceCurrency}</span>
                        {walletCurrency !== displayBalanceCurrency && (
                          <span className="text-xs text-yellow-600 block">
                            Original: {getCurrencySymbol(walletCurrency)}{availableBalance.toFixed(2)} {walletCurrency}
                          </span>
                        )}
                        <br />
                        {t("creditPaymentModal.needAdditional", { amount: `${getCurrencySymbol(displayBalanceCurrency)}${(planAmount - displayAvailableBalance).toFixed(2)} ${displayBalanceCurrency}` })}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Currency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("creditPaymentModal.currency")}
                    {isDetectingCurrency && (
                      <span className="ml-2 text-xs text-blue-600">
                        {t("creditPaymentModal.detectingCurrency")}
                      </span>
                    )}
                  </label>
                  <CurrencyDropdown
                    selectedCurrency={selectedCurrency}
                    onCurrencySelect={handleCurrencySelect}
                    placeholder={t("creditPaymentModal.selectCurrency")}
                    autoDetect={true}
                    disabled={isDetectingCurrency || loading || isFallbackPricing}
                  />
                  {isDetectingCurrency && (
                    <p className="mt-1 text-xs text-gray-500">
                      {t("creditPaymentModal.autoDetecting")}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {t("creditPaymentModal.currencyChangeNote", { defaultValue: "Change currency to see converted amounts" })}
                  </p>
                </div>

                {/* Payment Method Selection */}
                {!useExistingFunds && (
                  <PaymentMethodSelector
                    onPaymentMethodSelected={handlePaymentMethodSelected}
                    loading={loading}
                    selectedPaymentMethodId={selectedPaymentMethodId}
                  />
                )}

                {/* Payment Method Summary */}
                {useExistingFunds && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Wallet className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-green-800">{t("creditPaymentModal.paymentMethodTitle")}</h3>
                        <p className="text-sm text-green-700">{t("creditPaymentModal.usingWalletFunds")}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transaction Summary */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">{t("creditPaymentModal.transactionSummary")}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t("creditPaymentModal.amount")}</span>
                      <span className="font-medium">{resolvedCurrencySymbol}{planAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t("creditPaymentModal.platformFeeLabel")}</span>
                      <span className="font-medium text-green-600">{resolvedCurrencySymbol}{platformFee.toFixed(2)}</span>
                    </div>
                    {useExistingFunds && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{t("creditPaymentModal.paymentMethodLabel")}</span>
                        <span className="font-medium text-green-600">{t("creditPaymentModal.walletFunds")}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-medium border-t pt-2">
                      <span>{t("creditPaymentModal.total")}</span>
                      <span>{resolvedCurrencySymbol}{totalWithFee.toFixed(2)}</span>
                    </div>
                    {useExistingFunds && (
                      <div className="flex justify-between text-sm text-green-600 border-t pt-2">
                        <span>{t("creditPaymentModal.remainingBalance")}</span>
                        <span className="font-medium">{getCurrencySymbol(displayBalanceCurrency)}{(displayAvailableBalance - planAmount).toFixed(2)} {displayBalanceCurrency}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Message */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-red-50 border border-red-200 rounded-lg p-4"
                    >
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-sm text-red-700">
                            {error}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1 h-12"
                    disabled={loading}
                  >
                    {t("creditPaymentModal.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      loading ||
                      (!useExistingFunds && !selectedPaymentMethodId)
                    }
                    className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {useExistingFunds ? t("creditPaymentModal.processing") : t("creditPaymentModal.processingPayment")}
                      </>
                    ) : (
                      <>
                        {useExistingFunds ? (
                          <Wallet className="h-4 w-4 mr-2" />
                        ) : (
                          <CreditCard className="h-4 w-4 mr-2" />
                        )}
                        {useExistingFunds ? t("creditPaymentModal.purchaseWithWallet") : t("creditPaymentModal.purchaseCredits")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </motion.div>

    </div>,
    document.body
  );
};

// Wrapper component with Stripe Elements
const CreditPaymentModal: React.FC<CreditPaymentModalProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <CreditPaymentModalContent {...props} />
    </Elements>
  );
};

export default CreditPaymentModal;
